import { ref, type Ref, onMounted, onBeforeUnmount, nextTick } from "vue";

export function useElementRect<T extends Element>(elRef: Ref<T | null>) {
  const rect = ref<DOMRect | null>(null);

  let resizeObserver: ResizeObserver | null = null;
  let mutationObserver: MutationObserver | null = null;

  const updateRect = () => {
    if (elRef.value) {
      rect.value = elRef.value.getBoundingClientRect();
    }
  };

  const addListeners = () => {
    window.addEventListener("scroll", updateRect, true); // Capture scroll events in ancestors
    window.addEventListener("resize", updateRect);

    // MutationObserver watches for layout changes in the entire document
    mutationObserver = new MutationObserver(updateRect);
    mutationObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });
  };

  const removeListeners = () => {
    window.removeEventListener("scroll", updateRect, true);
    window.removeEventListener("resize", updateRect);
    mutationObserver?.disconnect();
  };

  onMounted(async () => {
    await nextTick();
    updateRect();

    // ResizeObserver: track changes to the element's size
    resizeObserver = new ResizeObserver(updateRect);
    if (elRef.value) {
      resizeObserver.observe(elRef.value);
    }

    addListeners();
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    removeListeners();
  });

  return rect;
}
